import React, { useState, useCallback, useRef, useEffect } from 'react';
import HomePage from './pages/HomePage/HomePage';
import Footer from './components/Footer/Footer';
import Navigation from './components/Navigation/Navigation';
import BoardPage from './pages/BoardPage/BoardPage';
import AboutUsPage from './pages/AboutUsPage/AboutUsPage';
import LanguageSwitcher from './components/LanguageSwitcher/LanguageSwitcher';
import AdminPage from './pages/AdminPage/AdminPage';
import { PageName } from './types';

function App() {
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  const [currentPage, setCurrentPage] = useState<PageName>('landing');
  const [scrollToSection, setScrollToSection] = useState<string | null>(null);
  const homePageRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleNavigate = (page: PageName, section?: string) => {
    setCurrentPage(page);
    setScrollToSection(section || null);
  };

  useEffect(() => {
    if (currentPage === 'home' && homePageRef.current) {
      // We don't need to force scroll to top for 'home' if we are just scrolling to a section
      // But if we switch from 'board' back to 'home', we might want to.
      // However, if we stay on 'home'/'landing' structure, this might be redundant or jarring if not careful.
      // Leaving it for now as it handles 'board' -> 'home' transition.
      // But for 'landing' -> 'home', scrollToSection handles it.
    }
  }, [currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight - 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollComplete = useCallback(() => {
    setScrollToSection(null);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'about':
      case 'home':
      case 'landing': // Render HomePage for landing to avoid re-mount flicker
      case 'join':
        return <HomePage onNavigate={handleNavigate} scrollToSection={scrollToSection} onScrollComplete={handleScrollComplete} />;
      case 'board':
        return <BoardPage />;
      case 'about-us':
        return <AboutUsPage />;
      default:
        return <HomePage onNavigate={handleNavigate} scrollToSection={scrollToSection} onScrollComplete={handleScrollComplete} />;
    }
  };

  // Determine variant based on page and scroll
  const getLanguageSwitcherVariant = () => {
    if (currentPage === 'landing') return 'transparent';
    if ((currentPage === 'home' || currentPage === 'about' || currentPage === 'join') && !isScrolled) return 'transparent';
    return 'solid';
  };

  if (isAdminRoute) {
    return <AdminPage />;
  }

  return (
    <div className="App min-h-screen flex flex-col">
      {/* Desktop Language Switcher - Fixed */}
      <div className="fixed top-4 right-4 z-50 hidden md:block">
        <LanguageSwitcher variant={getLanguageSwitcherVariant()} />
      </div>

      {currentPage !== 'landing' && <Navigation onNavigate={handleNavigate} />}

      <div className="flex-grow" ref={homePageRef}>
        {renderPage()}
      </div>
      
      {(currentPage === 'board' || currentPage === 'about-us') && <Footer />}
    </div>
  );
}

export default App;
